'use client'

/**
 * GridArchiveWrapper - Dual Query Architecture
 * =============================================
 *
 * This component implements a dual-query pattern to solve a pagination display issue.
 *
 * THE PROBLEM:
 * We want to display a different layout on page 1 vs subsequent pages:
 * - Page 1: 6 large featured items (3-col grid) + 25 regular items (5-col grid) = 31 items
 * - Page 2+: 25 regular items only (5-col grid)
 *
 * Using a single query with hitsPerPage=31 on page 1 and 25 on page 2+ would cause:
 * - Duplicate items appearing across pages
 * - Incorrect total page counts
 * - Stray items not filling rows properly (26 items ÷ 5 cols = 5 rows + 1 stray)
 *
 * THE SOLUTION:
 * Run two separate Algolia queries using <Index indexId="...">:
 *
 * 1. FEATURED QUERY (indexId="featured"):
 *    - Always fetches the first 6 items (hitsPerPage=6)
 *    - Has its own isolated search state - ignores pagination
 *    - Collects objectIDs and passes them up via onFeaturedIds
 *
 * 2. MAIN QUERY (default index context):
 *    - Fetches 25 items per page (hitsPerPage=25)
 *    - EXCLUDES the 6 featured IDs using "NOT objectID:..." filters
 *    - Responds to pagination normally
 *
 * WHY indexId MATTERS:
 * Without indexId, both <Index> components would share the same search state.
 * With indexId="featured", the featured query operates in a completely separate
 * namespace - it has its own pagination, filters, and results that don't interfere
 * with the main query.
 *
 * RESULT:
 * - Page 1: 6 featured + 25 main = 31 unique items
 * - Page 2+: 25 main items (different set, no duplicates)
 * - Clean 5-column grid with no stray items
 */

import { useRef, useState, useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { liteClient as algoliasearch } from 'algoliasearch/lite'
import { useAtomValue } from 'jotai'
import { Configure, Index, useInstantSearch } from 'react-instantsearch'
import { InstantSearchNext } from 'react-instantsearch-nextjs'

import GridArchiveInner from '@/components/grid-archive/GridArchiveInner'
import GridArchiveFeatured from '@/components/grid-archive/GridArchiveFeatured'
import { startDateAtom } from '@/components/filters/state'

import type { ArchiveAPIResponseType, FilterPropsType } from '@/layouts/types'

type GridProps = {
  algoliaIndex: string
  additionalTypes?: string[]
  filters?: FilterPropsType
  pillFilter?: string
  sidebarContent?: React.ReactNode
  pinnedPost?: ArchiveAPIResponseType['archive_pinned_post']
  queryString?: string
  disableFeatured?: boolean
  disableFilters?: boolean
}

const searchClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_APPLICATION_ID,
  process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY,
)

/**
 * PageStateTracker
 * Reads the current page from the main InstantSearch context and passes it up.
 * This is necessary because GridArchiveFeatured runs in a separate Index context
 * and can't access the main query's page state directly.
 */
function PageStateTracker({ onPageChange }: { onPageChange: (page: number) => void }) {
  const { results } = useInstantSearch()
  const currentPage = results?.page ?? 0

  useEffect(() => {
    onPageChange(currentPage)
  }, [currentPage, onPageChange])

  return null
}

export default function GridArchiveWrapper({
  algoliaIndex,
  additionalTypes,
  filters,
  pillFilter,
  sidebarContent,
  pinnedPost,
  queryString,
  disableFeatured,
  disableFilters,
}: GridProps): JSX.Element {
  const pathname = usePathname()
  const gridRef = useRef<HTMLDivElement>(null)
  const [featuredIds, setFeaturedIds] = useState<string[]>([])
  const [featuredRendered, setFeaturedRendered] = useState(disableFeatured ? true : false)
  const [currentPage, setCurrentPage] = useState(0)
  const startDate = useAtomValue(startDateAtom) ?? null
  const yesterdayUnix = Math.floor(Date.now() / 1000) - 86400
  const isFirstPage = currentPage === 0

  let configureFilters = ` hide_date_unix>${yesterdayUnix} `

  // add post type filters to the all posts index if multiple are set
  if (
    algoliaIndex.includes('wp_searchable_posts') &&
    Array.isArray(additionalTypes) &&
    additionalTypes.length > 0
  ) {
    const postTypeFilter = additionalTypes.map((type) => ` post_type:"all-${type}" `).join(' OR ')

    if (postTypeFilter?.length) {
      configureFilters += ` AND (${postTypeFilter}) `
    }
  }

  if (startDate) {
    configureFilters += ` AND event_date.start_date_unix >= ${startDate} `
  }

  // Allow custom algolia query string to be passed in to change the main query
  if (queryString) {
    configureFilters += queryString
  }

  // Build exclusion filter for main query (exclude featured items)
  // This prevents the same items appearing in both the featured grid and main grid
  const exclusionFilter = featuredIds.length
    ? featuredIds.map((id) => `NOT objectID:"${id}"`).join(' AND ')
    : ''

  const mainFilters = exclusionFilter
    ? `${configureFilters} AND ${exclusionFilter}`
    : configureFilters

  // console.log('Final index', algoliaIndex)
  // console.log('Final filters', configureFilters)
  // console.log('Featured IDs to exclude', featuredIds)

  return (
    <InstantSearchNext
      searchClient={searchClient}
      indexName={algoliaIndex}
      routing
      future={{
        preserveSharedStateOnUnmount: true,
        persistHierarchicalRootCount: true,
      }}>
      {/* Main grid query - 25 items, excludes featured */}
      <Configure hitsPerPage={25} filters={mainFilters} />
      <PageStateTracker onPageChange={setCurrentPage} />

      <GridArchiveInner
        filters={filters}
        pillFilter={pillFilter}
        pinnedPost={pinnedPost}
        gridRef={gridRef}
        featuredIds={featuredIds}
        featuredRendered={featuredRendered}
        disableFilters={disableFilters}
        disableFeatured={disableFeatured}
        pathname={pathname}
        featuredContent={
          disableFeatured ? null : (
            <Index indexName={algoliaIndex} indexId="featured">
              <Configure hitsPerPage={6} filters={configureFilters} />
              <GridArchiveFeatured
                onFeaturedIds={setFeaturedIds}
                sidebarContent={sidebarContent}
                isFirstPage={isFirstPage}
                onFeaturedRendered={setFeaturedRendered}
              />
            </Index>
          )
        }
      />
    </InstantSearchNext>
  )
}
